import axios from 'axios';
import { getCoreDomain } from './nuiText';

/**
 * Вход через соцсеть со страницы, работающей в iframe витрины.
 *
 * Страницы провайдеров нельзя открывать в iframe, поэтому авторизация идёт в
 * отдельном popup-окне на app.*, а обменом кода на сессию занимается уже эта
 * страница - её запрос идёт через proxy.php, поэтому Set-Cookie приходит от
 * имени витрины и кука остаётся своей для домена в адресной строке. Приди она
 * напрямую с app.*, браузер счёл бы её сторонней и мог бы отбросить.
 *
 * Ответ из popup приходит двумя путями сразу:
 *   - postMessage в opener - быстрый;
 *   - опрос /oauth/exchange по nonce - надёжный, на случай если COOP на
 *     странице провайдера разорвала window.opener.
 * Побеждает тот, кто успел: оба токена на сервере одноразовые, поэтому
 * опоздавший получит 401, а `done` не даст вызвать колбэк дважды.
 */

const POLL_INTERVAL_MS = 1500;
const OVERALL_TIMEOUT_MS = 180000;
// Пользователь закрыл окно сам - даём последнему запросу долететь и сдаёмся.
const GRACE_AFTER_CLOSE_MS = 5000;

/** Origin приложения. В iframe hostname - это витрина, поэтому app-хост
 * выводится из неё; при прямом открытии страницы app.* или localhost адрес
 * берётся как есть. */
export function appOrigin() {
  const host = window.location.host;
  if (host.startsWith('localhost') || host.startsWith('app.')) {
    return `${window.location.protocol}//${host}`;
  }
  return `https://app.${getCoreDomain()}`;
}

function newNonce() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  const buf = new Uint8Array(16);
  window.crypto.getRandomValues(buf);
  return Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
}

const postConfig = {
  headers: { 'X-Requested-With': 'HttpRequest',
             'Csrf-Token': 'nocheck' },
  timeout: 0
};

/**
 * params:
 *   provider          - 'google'
 *   lang              - код языка интерфейса
 *   residenceCountry  - alpha2 юрисдикции регистрации
 *   onSuccess()       - сессия выдана, кука уже стоит
 *   onError(reason)   - не получилось; reason - короткий код
 */
export function startOAuth(params) {
  const provider = params.provider;
  const nonce = newNonce();
  const url = `${appOrigin()}/auth/${provider}/start` +
        `?nonce=${encodeURIComponent(nonce)}` +
        `&lang=${encodeURIComponent(params.lang || 'en')}` +
        `&residenceCountry=${encodeURIComponent(params.residenceCountry || '')}`;

  const popup = window.open(
    url, `oauth_${provider}`,
    'width=520,height=640,menubar=no,toolbar=no,location=no,status=no');
  if (popup == null) {
    params.onError('popup_blocked');
    return;
  }

  let done = false;
  let pollTimer = null;
  let closedAt = null;
  const startedAt = Date.now();

  const cleanup = () => {
    window.removeEventListener('message', onMessage);
    if (pollTimer != null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const succeed = () => {
    if (done) return;
    done = true;
    cleanup();
    params.onSuccess();
  };

  const fail = (reason) => {
    if (done) return;
    done = true;
    cleanup();
    params.onError(reason);
  };

  // Кода может не быть - тогда обмениваем по nonce.
  const exchange = (payload) => {
    const form = new URLSearchParams();
    Object.keys(payload).forEach(k => form.append(k, payload[k]));
    return axios.post('/oauth/exchange', form, postConfig)
      .then(response => {
        if (response.status === 200) {
          succeed();
          return true;
        }
        return false;
      })
      .catch(() => false);
  };

  const onMessage = (event) => {
    // Сообщение приходит СО СТРАНИЦЫ app.*, а не с витрины: её и проверяем.
    if (event.origin !== appOrigin()) return;
    const data = event.data || {};
    if (data.provider !== provider) return;
    if (data.message === 'oauthLogin' && data.loginCode) {
      exchange({ code: data.loginCode });
    } else if (data.message === 'oauthError') {
      fail(data.error || 'failed');
    }
  };

  window.addEventListener('message', onMessage);

  pollTimer = setInterval(() => {
    if (done) return;
    if (Date.now() - startedAt > OVERALL_TIMEOUT_MS) {
      fail('timeout');
      return;
    }
    // popup.closed читается без обращения к его документу, поэтому доступно и
    // после ухода на чужой origin.
    if (popup.closed) {
      if (closedAt == null) {
        closedAt = Date.now();
      } else if (Date.now() - closedAt > GRACE_AFTER_CLOSE_MS) {
        fail('cancelled');
        return;
      }
    }
    exchange({ nonce: nonce });
  }, POLL_INTERVAL_MS);
}

/** Список провайдеров, включённых на этом деплое. Сервер печатает его в
 * страницу, поэтому кнопка не появится там, где роут всё равно ответит 404. */
export function enabledProviders() {
  const box = document.getElementById('oauthProviders');
  if (box == null) return [];
  return box.innerHTML.split(',').map(s => s.trim()).filter(s => s.length > 0);
}
